import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../contexts/LocaleContext';
import type { ProjectListItem } from '../../services/adminApi';
import { listProjects } from '../../services/adminApi';
import ProjectList from './ProjectList';
import ProjectDetail from './ProjectDetail';
import styles from './AdminPage.module.css';

/**
 * Admin shell: project list <-> project detail, driven by local state.
 */
const AdminPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const { selectedLocale } = useLocale();

  const t = (en: string, ru: string) => (selectedLocale === 'ru' ? ru : en);

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const refresh_projects = useCallback(async () => {
    try {
      const data = await listProjects();
      setProjects(data.projects);
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh_projects();
  }, [refresh_projects]);

  return (
    <div className={styles.admin_shell}>
      <header className={styles.admin_header}>
        <div className={styles.admin_branding}>
          <img
            src="/diana_logo_monochrome.png"
            alt="Diana Lapteva"
            className={styles.admin_logo}
          />
          <div>
            <h1 className={styles.admin_heading}>
              {t('Admin Panel', 'Панель администратора')}
            </h1>
            <p className={styles.admin_user}>
              {t('Signed in as', 'Вы вошли как')} {user?.username ?? '—'}
            </p>
          </div>
        </div>
        <button type="button" className={styles.logout_button} onClick={signOut}>
          {t('Log out', 'Выйти')}
        </button>
      </header>

      <main className={styles.admin_body}>
        {selectedProjectId ? (
          <ProjectDetail
            projectId={selectedProjectId}
            onBack={() => {
              setSelectedProjectId(null);
              refresh_projects();
            }}
          />
        ) : (
          <ProjectList
            projects={projects}
            isLoading={isLoading}
            error={error}
            onOpenProject={setSelectedProjectId}
            onRefresh={refresh_projects}
          />
        )}
      </main>
    </div>
  );
};

export default AdminPage;
